import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import LinkOutlinedIcon from "@material-ui/icons/Link";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import { toast } from "react-toastify";
import { getHomeById } from "src/actions/HomeActions";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { connect } from "react-redux";
import api from "src/utils/api";

// ─── Design tokens (shared with ExperienceListResults) ─────────────────────
const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  borderStrong: "#E5E7EB",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  divider: "#F1F5F9",
};

const useStyles = makeStyles(() => ({
  card: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    overflow: "hidden",
  },
  tableHead: {
    backgroundColor: "#FAFBFC",
  },
  tableHeadCell: {
    color: TOKENS.textSecondary,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${TOKENS.border}`,
    padding: "14px 20px",
  },
  tableRow: {
    transition: "background 0.15s ease",
    "&:hover": { backgroundColor: "#FAFBFC" },
  },
  tableCell: {
    padding: "16px 20px",
    borderBottom: `1px solid ${TOKENS.divider}`,
    fontSize: 14,
    color: TOKENS.textPrimary,
    verticalAlign: "middle",
  },
  titleCell: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  thumb: {
    width: 44,
    height: 44,
    borderRadius: 10,
    flexShrink: 0,
    backgroundColor: TOKENS.divider,
  },
  thumbFallback: {
    width: 44,
    height: 44,
    borderRadius: 10,
    flexShrink: 0,
    backgroundColor: "#F2F2F2",
    color: TOKENS.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
  },
  titleText: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKENS.textPrimary,
    lineHeight: 1.3,
  },
  titleSub: {
    fontSize: 12,
    color: TOKENS.textMuted,
    marginTop: 2,
  },
  priceText: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKENS.textPrimary,
  },
  priceUnit: {
    fontSize: 12,
    color: TOKENS.textMuted,
    fontWeight: 400,
    marginLeft: 4,
  },
  typePill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: "#EEF2FF",
    color: "#000000",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
  },
  statusApproved: { backgroundColor: "#ECFDF5", color: "#059669" },
  statusPending: { backgroundColor: "#FEF3C7", color: "#B45309" },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  actions: {
    display: "flex",
    gap: 4,
    justifyContent: "flex-end",
  },
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    color: TOKENS.textSecondary,
    "&:hover": {
      backgroundColor: TOKENS.divider,
      color: TOKENS.textPrimary,
    },
  },
  iconBtnDanger: {
    width: 32,
    height: 32,
    borderRadius: 8,
    color: TOKENS.textSecondary,
    "&:hover": {
      backgroundColor: "#FEF2F2",
      color: "#EF4444",
    },
  },
  emptyState: {
    padding: "64px 20px",
    textAlign: "center",
  },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: "#F2F2F2",
    color: TOKENS.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    fontSize: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    marginBottom: 4,
  },
  emptySub: {
    fontSize: 13,
    color: TOKENS.textSecondary,
  },
  pagination: {
    borderTop: `1px solid ${TOKENS.border}`,
    "& .MuiTablePagination-toolbar": {
      paddingLeft: 16,
      paddingRight: 8,
    },
    "& .MuiTablePagination-caption": {
      fontSize: 13,
      color: TOKENS.textSecondary,
    },
  },
}));

const HomeListResults = ({ homes = [], getHomeById, home, ...rest }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);

  const onDelete = async (homeId) => {
    try {
      await api.delete(`homes/host/home/${homeId}`);
      window.location.reload();
    } catch (error) {
      toast.error("Action failed!");
    }
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePageChange = (_event, newPage) => setPage(newPage);

  const viewHome = async (homeId) => {
    await getHomeById(homeId, navigate);
  };

  const viewCalendar = async (homeId) => {
    await getHomeById(homeId);
  };

  const paged = homes.slice(page * limit, page * limit + limit);

  return (
    <Card className={classes.card} {...rest}>
      {homes.length === 0 ? (
        <Box className={classes.emptyState}>
          <Box className={classes.emptyIcon}>—</Box>
          <Typography className={classes.emptyTitle}>No homes yet</Typography>
          <Typography className={classes.emptySub}>
            When you list a home, it'll show up here.
          </Typography>
        </Box>
      ) : (
        <>
          <PerfectScrollbar>
            <Box style={{ minWidth: 900 }}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>
                      Home
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Type
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Price
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Space
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Status
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((home) => (
                    <TableRow hover key={home.id} className={classes.tableRow}>
                      {/* Title + thumbnail */}
                      <TableCell className={classes.tableCell}>
                        <Box className={classes.titleCell}>
                          {home.coverImage_URL ? (
                            <Avatar
                              src={home.coverImage_URL}
                              variant="rounded"
                              className={classes.thumb}
                            />
                          ) : (
                            <Box className={classes.thumbFallback}>
                              {home.title?.[0]?.toUpperCase() || "H"}
                            </Box>
                          )}
                          <Box>
                            <Typography className={classes.titleText}>
                              {home.title || "Untitled"}
                            </Typography>
                            {home.district && (
                              <Typography className={classes.titleSub}>
                                {home.district}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Type */}
                      <TableCell className={classes.tableCell}>
                        <span className={classes.typePill}>
                          {home.home_type || "—"}
                        </span>
                      </TableCell>

                      {/* Price */}
                      <TableCell className={classes.tableCell}>
                        <span className={classes.priceText}>{home.price}</span>
                        <span className={classes.priceUnit}>USD/night</span>
                      </TableCell>

                      {/* Space */}
                      <TableCell className={classes.tableCell}>
                        {home.spaceToUse || "—"}
                      </TableCell>

                      {/* Status */}
                      <TableCell className={classes.tableCell}>
                        <span
                          className={`${classes.statusPill} ${
                            home.approved
                              ? classes.statusApproved
                              : classes.statusPending
                          }`}
                        >
                          <span
                            className={classes.statusDot}
                            style={{
                              backgroundColor: home.approved
                                ? "#10B981"
                                : "#F59E0B",
                            }}
                          />
                          {home.approved ? "Approved" : "Pending"}
                        </span>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className={classes.tableCell} align="right">
                        <Box className={classes.actions}>
                          <Tooltip title="Edit">
                            <IconButton
                              className={classes.iconBtn}
                              onClick={() => viewHome(home.id)}
                              size="small"
                            >
                              <EditOutlinedIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Calendar">
                            <IconButton
                              className={classes.iconBtn}
                              onClick={() => viewCalendar(home.id)}
                              size="small"
                            >
                              <EventOutlinedIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>

                          {home.approved && (
                            <CopyToClipboard
                              text={`https://tubayo.com/homes/${home.id}`}
                              onCopy={() => toast.success("Link copied!")}
                            >
                              <Tooltip title="Copy public link">
                                <IconButton
                                  className={classes.iconBtn}
                                  size="small"
                                >
                                  <LinkOutlinedIcon style={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </CopyToClipboard>
                          )}

                          <Tooltip title="Delete">
                            <IconButton
                              className={classes.iconBtnDanger}
                              size="small"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Are you sure you wish to delete this Home?",
                                  )
                                )
                                  onDelete(home.id);
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </PerfectScrollbar>

          <TablePagination
            component="div"
            className={classes.pagination}
            count={homes.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      )}
    </Card>
  );
};

HomeListResults.propTypes = {
  homes: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  home: state.homes.home,
});

export default connect(mapStateToProps, { getHomeById })(HomeListResults);
